import { RefreshCw, BarChart3 } from 'lucide-react'
import ErrorMessage from '../ui/ErrorMessage'
import useOverviewData from './overview/useOverviewData'
import OperatorCard from './overview/OperatorCard'
import OperatorSummaryBanner from './overview/OperatorSummaryBanner'
import DataFreshnessHeader from './overview/DataFreshnessHeader'
import OverviewSkeleton from './overview/OverviewSkeleton'
import DailySalesChart from './overview/DailySalesChart'
import TotalGoalsSummary from './overview/TotalGoalsSummary'

const Overview = () => {
  const {
    loadingCore,
    loadingGoals,
    loadingTrends,
    error,
    operatorStatus,
    visualizationData,
    stockExpositionData,
    salonAttributesData,
    dataFreshness,
    freshnessThresholds,
    refreshingCache,
    handleRefreshCache,
    getCoverageLabel,
    getCoverage30dLabel,
    getCoverageValue,
    getGoalColor,
    getAvgSales,
    dailySalesData,
    loadingDailySales,
    dailySalesDays,
    loadMoreDailySales,
    productGroupNames
  } = useOverviewData()

  const helpers = {
    getCoverageLabel,
    getCoverage30dLabel,
    getCoverageValue,
    getGoalColor,
    getAvgSales
  }

  // Show skeleton only while core data is loading
  if (loadingCore) {
    return <OverviewSkeleton />
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error} 
        onRetry={() => window.location.reload()}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-brand-gray-900 flex items-center">
          <BarChart3 className="h-6 w-6 mr-2 text-brand-blue" />
          Przegląd Operatorów
        </h2>
        <button
          onClick={handleRefreshCache}
          disabled={refreshingCache}
          className={`
            flex items-center space-x-2 px-5 py-2.5 rounded-lg font-semibold transition-all shadow-sm
            ${refreshingCache
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
          `}
          title="Odśwież dane (usuń cache)"
        >
          <RefreshCw className={`h-4 w-4 ${refreshingCache ? 'animate-spin' : ''}`} />
          <span>{refreshingCache ? 'Odświeżanie...' : 'Odśwież'}</span>
        </button>
      </div>

      {/* Operators Summary Banner */}
      <OperatorSummaryBanner operators={operatorStatus} />

      {/* Total Goals Summary */}
      <TotalGoalsSummary operators={operatorStatus} loadingGoals={loadingGoals} />

      {/* Data Freshness Information */}
      <DataFreshnessHeader 
        dataFreshness={dataFreshness} 
        thresholds={freshnessThresholds} 
        operatorOrder={operatorStatus.map(op => op.code)}
      />

      {/* Detailed Operator Status Cards */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-gray-800 flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-brand-blue" />
            Status per operator
          </h2>
          <a
            href="#daily-sales"
            className="text-sm text-blue-500 hover:text-blue-700 hover:underline transition-colors"
          >
            Sprzedaz dzienna &darr;
          </a>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {operatorStatus.map((operator) => (
            <OperatorCard
              key={operator.code}
              operator={operator}
              stockExpositionData={stockExpositionData}
              salonAttributesData={salonAttributesData}
              visualizationData={visualizationData}
              helpers={helpers}
              loadingGoals={loadingGoals}
              loadingTrends={loadingTrends}
            />
          ))}
        </div>
      </section>

      {/* Daily Sales Chart */}
      <section id="daily-sales">
        <DailySalesChart
          data={dailySalesData}
          loading={loadingDailySales}
          currentDays={dailySalesDays}
          onChangeDays={loadMoreDailySales}
          operators={operatorStatus}
          groupNames={productGroupNames}
        />
      </section>
    </div>
  )
}

export default Overview
